import { Injectable, inject } from '@angular/core';
import { ContextInterface, EduContent, EduContentInterface, ResultInterface, ScormCmiMode } from '@campus/dal';
import {
  BrowseExerciseConfigurationInterface,
  CustomExerciseServiceInterface,
  ExerciseConfiguration,
  ExercisePlayerOptionsInterface,
  NormalExerciseConfigurationInterface,
  ReviewExerciseConfigurationInterface,
} from '@campus/exercise';
import { GeogebraOpenerService } from '../geogebra-opener.service';

@Injectable({
  providedIn: 'root',
})
export class GeogebraExerciseService implements CustomExerciseServiceInterface {
  private geogebraOpenerService = inject(GeogebraOpenerService);

  public isContentCompatible(content: EduContentInterface): boolean {
    const eduContent = content instanceof EduContent ? content : EduContent.toEduContent(content);
    return eduContent.isGeogebra;
  }

  public loadExercise(data: ExerciseConfiguration) {
    const { mode, eduContent } = data;
    const eduContentInstance = eduContent instanceof EduContent ? eduContent : EduContent.toEduContent(eduContent);

    switch (mode) {
      case ScormCmiMode.CMI_MODE_NORMAL: {
        const { userId, playerOptions, context, result } = data as NormalExerciseConfigurationInterface;
        return this.startExerciseFromContext(eduContentInstance, result, context, playerOptions, userId);
      }
      case ScormCmiMode.CMI_MODE_REVIEW: {
        const { result, context } = data as ReviewExerciseConfigurationInterface;
        return this.reviewExerciseFromResult(eduContentInstance, result, context);
      }
      case ScormCmiMode.CMI_MODE_BROWSE: {
        const { context } = data as BrowseExerciseConfigurationInterface;
        return this.previewExercise(eduContentInstance, context);
      }

      default:
        console.warn(`Mode <<${mode}>> is not supported by the ScormExerciseService`);
    }
  }

  public shouldGetResult(data: ExerciseConfiguration): boolean {
    if (data.mode === ScormCmiMode.CMI_MODE_REVIEW) return true;
    if (data.mode === ScormCmiMode.CMI_MODE_GRADING) return true;

    if (data.mode !== ScormCmiMode.CMI_MODE_NORMAL) return false;

    if (data.result) return false;
    if (data.playerOptions?.localSession) return false;

    return true;
  }

  private startExerciseFromContext(
    eduContent: EduContent,
    result: ResultInterface,
    context: ContextInterface,
    playerOptions: ExercisePlayerOptionsInterface,
    userId: number
  ) {
    const geogebraXml = this.getInitialData(eduContent, result);

    return this.geogebraOpenerService.openExercise(geogebraXml, {
      ...context,
      saveToApi: !playerOptions.localSession,
      personId: userId,
      eduContentId: eduContent.id,
      result,
    });
  }

  private reviewExerciseFromResult(eduContent: EduContent, result: ResultInterface, context: ContextInterface = {}) {
    const geogebraXml = this.getInitialData(eduContent, result);

    return this.geogebraOpenerService.openExercise(geogebraXml, {
      ...context,
      saveToApi: false,
      eduContentId: eduContent.id,
    });
  }

  private previewExercise(eduContent: EduContent, context: ContextInterface) {
    const geogebraXml = this.getInitialData(eduContent);

    return this.geogebraOpenerService.openExercise(geogebraXml, {
      ...context,
      saveToApi: false,
      eduContentId: eduContent.id,
    });
  }

  private getInitialData(eduContent: EduContent, result?: ResultInterface): string {
    if (!result) return eduContent.geogebraData;
    if (!result.cmi) return eduContent.geogebraData;

    const parsedCmi = typeof result.cmi === 'string' ? JSON.parse(result.cmi) : result.cmi;
    return JSON.stringify(parsedCmi.data);
  }
}
